import React, { useEffect, useMemo, useState } from "react";
import "./UsersManager.css";

const API_URL = "https://cms.amservices.eu/api/ehv_users_manager.php";
const EMPTY_FORM = {
  id: null, username: "", password: "", role: "user", personne_id: "", is_active: true,
  can_access_planning: true, can_access_kanban: true, can_access_fiches: true,
  can_view_finance: false, can_manage_finance: false,
};
const ROLES = {
  user: { label: "Utilisateur", icon: "🟢" },
  maitre: { label: "Maître", icon: "🔵" },
  master: { label: "Maître", icon: "🔵" },
  admin: { label: "Administrateur", icon: "🔴" },
  disabled: { label: "Désactivé", icon: "⚫" },
};
const ACCESS_FIELDS = [
  ["can_access_planning", "Planning", "Accès au planning hebdomadaire"],
  ["can_access_kanban", "Kanban", "Accès aux tâches et aux étapes"],
  ["can_access_fiches", "Fiches terrain", "Création, signature et PDF"],
  ["can_view_finance", "Voir la facturation", "Consultation des articles et brouillons"],
  ["can_manage_finance", "Gérer la facturation", "Calcul, synchronisation et brouillons Henrri"],
];

function token() {
  const user = JSON.parse(localStorage.getItem("am_user") || "null");
  return localStorage.getItem("am_token") || user?.token || "";
}
function formatDate(value) {
  if (!value) return "Jamais";
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString("fr-FR");
}
function hasAccess(user, key) { return Number(user?.[key]) === 1; }

export default function EhvUsersManager({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter((user) => [user.id, user.username, user.role, user.personne_id]
      .some((value) => String(value || "").toLowerCase().includes(query)));
  }, [users, search]);

  const request = async (payload = null) => {
    const authToken = token();
    const response = await fetch(API_URL, {
      method: payload ? "POST" : "GET",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}`, "X-Auth-Token": authToken },
      ...(payload ? { body: JSON.stringify(payload) } : {}),
    });
    const text = await response.text();
    let data;
    try { data = text ? JSON.parse(text) : {}; } catch { throw new Error("Réponse invalide du serveur."); }
    if (!response.ok || !data.success) throw new Error(data.message || data.error || `Erreur HTTP ${response.status}`);
    return data;
  };

  const loadUsers = async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError("");
    try { const data = await request(); setUsers(Array.isArray(data.users) ? data.users : []); }
    catch (err) { setError(err.message || "Impossible de charger les utilisateurs EHV."); }
    finally { if (showLoader) setLoading(false); }
  };
  useEffect(() => { loadUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const edit = (user) => {
    const active = Number(user.is_active) === 1;
    setForm({
      id: Number(user.id), username: user.username || "", password: "",
      role: user.role === "disabled" ? "user" : (user.role === "master" ? "maitre" : (user.role || "user")), personne_id: user.personne_id || "", is_active: active,
      ...Object.fromEntries(ACCESS_FIELDS.map(([key]) => [key, hasAccess(user, key)])),
    });
    setError(""); setMessage("");
  };

  const submit = async (event) => {
    event.preventDefault(); setSaving(true); setError(""); setMessage("");
    try {
      const rights = Object.fromEntries(ACCESS_FIELDS.map(([key]) => [key, Boolean(form[key])]));
      if (form.can_manage_finance) rights.can_view_finance = true;
      const payload = { username: form.username.trim(), role: form.role, personne_id: form.personne_id, is_active: form.is_active, ...rights };
      if (form.id) {
        await request({ action: "update", user_id: form.id, ...payload });
        if (form.password) await request({ action: "password", user_id: form.id, password: form.password });
      } else {
        await request({ action: "create", password: form.password, ...payload });
      }
      setMessage(form.id ? "Utilisateur EHV et accès mis à jour." : "Utilisateur EHV créé et synchronisé avec ses modules.");
      setForm(EMPTY_FORM); await loadUsers(false);
    } catch (err) { setError(err.message || "L’enregistrement a échoué."); }
    finally { setSaving(false); }
  };

  const resetPassword = async (user) => {
    const password = window.prompt(`Nouveau mot de passe pour ${user.username} (10 caractères minimum)`);
    if (!password) return;
    setSaving(true); setError(""); setMessage("");
    try { await request({ action: "password", user_id: Number(user.id), password }); setMessage(`Mot de passe de ${user.username} remplacé.`); }
    catch (err) { setError(err.message || "Le mot de passe n’a pas pu être modifié."); }
    finally { setSaving(false); }
  };

  const toggleAccess = (key, checked) => {
    const next = { ...form, [key]: checked };
    if (key === "can_manage_finance" && checked) next.can_view_finance = true;
    if (key === "can_view_finance" && !checked) next.can_manage_finance = false;
    setForm(next);
  };
  const masterRole = ["admin", "master", "maitre"].includes(form.role);

  return (
    <main className="users-manager-page users-manager-ehv">
      <header className="users-manager-header">
        <div className="users-manager-title"><span className="um-eyebrow">Administration · EHV</span><h1>Utilisateurs Énergies Hautes Vosges</h1><p>Création du compte CRM et synchronisation automatique vers le Kanban et le Planning EHV.</p></div>
        <div className="um-current-admin"><span>Administrateur CMS connecté</span><strong>{currentUser?.nom || currentUser?.login}</strong></div>
      </header>
      <section className="um-summary" aria-label="Résumé EHV">
        <article><strong>{users.length}</strong><span>comptes EHV</span></article>
        <article><strong>{users.filter((user) => Number(user.is_active) === 1).length}</strong><span>comptes actifs</span></article>
        <article><strong>{users.filter((user) => Number(user.can_manage_finance) === 1).length}</strong><span>gestionnaires facturation</span></article>
      </section>
      {message && <div className="um-alert um-alert-success">{message}</div>}
      {error && <div className="um-alert um-alert-error">{error}</div>}
      <div className="um-content-grid">
        <section className="um-panel um-form-panel">
          <div className="um-panel-title"><div><span className="um-step">{form.id ? `Compte #${form.id}` : "Nouveau compte"}</span><h2>{form.id ? "Modifier l’utilisateur" : "Ajouter un utilisateur EHV"}</h2></div>{form.id && <button className="um-link-button" type="button" onClick={() => setForm(EMPTY_FORM)}>Annuler</button>}</div>
          <form onSubmit={submit}>
            <div className="um-form-group"><label htmlFor="ehv-username">Identifiant</label><input id="ehv-username" className="um-input" pattern="[A-Za-z0-9._-]{3,50}" value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} required /></div>
            <div className="um-form-group"><label htmlFor="ehv-password">{form.id ? "Nouveau mot de passe (facultatif)" : "Mot de passe initial"}</label><input id="ehv-password" className="um-input" type="password" minLength={10} value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder={form.id ? "Vide = inchangé" : "10 caractères minimum"} required={!form.id} autoComplete="new-password" /></div>
            <div className="um-form-group"><label htmlFor="ehv-role">Rôle</label><select id="ehv-role" className="um-select" value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })}><option value="user">🟢 Utilisateur</option><option value="maitre">🔵 Maître</option><option value="admin">🔴 Administrateur</option></select><span className="um-hint">Les rôles Maître et Administrateur possèdent tous les accès.</span></div>
            <div className="um-form-group"><label htmlFor="ehv-personne">ID personne planning (facultatif)</label><input id="ehv-personne" className="um-input" type="number" min="1" value={form.personne_id} onChange={(event) => setForm({ ...form, personne_id: event.target.value })} /></div>
            <fieldset className="um-assignment-fieldset" disabled={masterRole}><legend>Applications autorisées</legend>{ACCESS_FIELDS.map(([key, label, description]) => <label className="um-check-card" key={key}><input type="checkbox" checked={masterRole || Boolean(form[key])} onChange={(event) => toggleAccess(key, event.target.checked)} /><span><strong>{label}</strong><small>{description}</small></span></label>)}</fieldset>
            {form.id && <label className="um-check-card"><input type="checkbox" checked={form.is_active} onChange={(event) => setForm({ ...form, is_active: event.target.checked })} /><span><strong>Compte actif</strong><small>Décochez pour bloquer toutes les nouvelles connexions.</small></span></label>}
            <button className="um-btn um-btn-primary" type="submit" disabled={saving}>{saving ? "Enregistrement…" : form.id ? "Enregistrer les modifications" : "Créer le compte EHV"}</button>
          </form>
        </section>
        <section className="um-panel um-list-panel">
          <div className="um-toolbar"><div><span className="um-step">Annuaire</span><h2>Équipe EHV</h2></div><input className="um-input um-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Identifiant, rôle ou ID…" /></div>
          {loading ? <div className="um-loading">Chargement des comptes…</div> : <div className="um-table-wrap"><table className="um-table"><thead><tr><th>Utilisateur</th><th>Rôle</th><th>Accès</th><th>Dernière connexion</th><th>Actions</th></tr></thead><tbody>
            {filteredUsers.map((user) => { const role=ROLES[user.role]||ROLES.user;const access=[hasAccess(user,"can_access_planning")&&"Planning",hasAccess(user,"can_access_kanban")&&"Kanban",hasAccess(user,"can_access_fiches")&&"Fiches",hasAccess(user,"can_view_finance")&&(hasAccess(user,"can_manage_finance")?"Facturation gestion":"Facturation lecture")].filter(Boolean);return <tr key={user.id}><td><div className="um-user-cell"><span className="um-avatar ehv">{String(user.username).charAt(0).toUpperCase()}</span><span><strong>{user.username}</strong><small>#{user.id}{user.personne_id?` · personne #${user.personne_id}`:""}</small></span></div></td><td><span className={`um-role um-role-${user.role}`}>{role.icon} {role.label}</span>{Number(user.is_active)!==1&&<small className="um-inactive">Désactivé</small>}</td><td><div className="um-access-tags">{access.map((label)=><span key={label}>{label}</span>)}{!access.length&&<span className="none">Aucun</span>}</div></td><td className="um-date">{formatDate(user.last_login)}</td><td><div className="um-row-actions"><button className="um-btn um-btn-warning" type="button" onClick={() => edit(user)} disabled={saving}>Modifier</button><button className="um-btn um-btn-sync" type="button" onClick={() => resetPassword(user)} disabled={saving}>Mot de passe</button></div></td></tr>; })}
            {!filteredUsers.length && <tr><td colSpan="5" className="um-empty">Aucun utilisateur EHV trouvé.</td></tr>}
          </tbody></table></div>}
        </section>
      </div>
    </main>
  );
}
